import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

/* -----------------------------
   UPDATED + EXPANDED FAQ DATA
------------------------------ */
const faqData = [
  {
    q: "How do employees mark attendance?",
    a: `Employees can mark attendance through mobile or desktop using GPS-verified punch-in,
selfie validation, and device integrity checks. The system automatically prevents spoofing,
duplicate entries, and location mismatch errors. Offline punches sync automatically once network
is restored.`,
  },
  {
    q: "Does the HRMS support fully automated payroll?",
    a: `Yes. Payroll is auto-generated every month using company policies, attendance data, overtime,
leave encashments, statutory rules, arrears, bonuses, and revision cycles. The system eliminates
manual calculations and maintains PF/ESI/PT/TDS accuracy across all employee bands.`,
  },
  {
    q: "Can managers review and approve requests easily?",
    a: `Absolutely. Managers get a unified approval console where they can review leaves,
attendance correction requests, shift changes, reimbursements, WFH applications,
and OTs—all in one place. Every action is tracked, time-stamped, and logged for
audit and transparency.`,
  },
  {
    q: "How does HRMS ensure transparency and accountability?",
    a: `Every HR or Admin action is recorded in an audit-safe event log. This includes profile edits,
salary updates, attendance changes, permission alterations, and workflow approvals.
Admins can trace the complete lifecycle of any modification at any time.`,
  },
  {
    q: "Is the HRMS optimized for mobile use?",
    a: `Yes. The interface is fully mobile-responsive, giving employees the ability to access
attendance, payslips, leave balances, notifications, helpdesk, and approvals
smoothly from any device—Android, iOS, tablet, or desktop.`,
  },
  {
    q: "How secure is employee data inside the system?",
    a: `All sensitive information is protected using AES-256 encryption, strict role-based access,
multi-layer authentication flows, and periodic security audits. Data is stored in secure
isolated environments with restricted internal access.`,
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section
      className="py-24 bg-white border-t border-gray-100"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      <div className="max-w-5xl mx-auto px-6">
        
        {/* HEADER */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center text-3xl md:text-4xl font-semibold text-gray-900"
        >
          Frequently Asked Questions
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center text-gray-600 max-w-xl mx-auto mt-3 text-base leading-relaxed"
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          Clear explanations to help employees, managers, and HR teams get the most out of the HRMS.
        </motion.p>

        {/* FAQ LIST */}
        <div className="mt-12 space-y-4">
          {faqData.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="
                border border-gray-200 
                rounded-2xl 
                overflow-hidden 
                bg-white 
                shadow-sm 
                hover:shadow-md 
                transition-all
              "
            >
              {/* QUESTION HEADER */}
              <button
                onClick={() => toggle(i)}
                className="
                  w-full 
                  text-left 
                  px-6 py-5 
                  flex justify-between items-center 
                  focus:outline-none
                  hover:bg-gray-50
                  transition
                "
              >
                <span className="text-gray-900 font-medium text-base">
                  {item.q}
                </span>

                <ChevronDownIcon
                  className={`
                    w-6 h-6 text-gray-600 
                    transition-transform duration-300 
                    ${openIndex === i ? "rotate-180" : ""}
                  `}
                />
              </button>

              {/* ANSWER */}
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="px-6 pb-6"
                  >
                    <p
                      className="text-gray-600 text-sm leading-relaxed"
                      style={{ fontFamily: "Roboto, sans-serif" }}
                    >
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
